import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import {
  List,
  ListItemButton,
  ListItemText,
  ListItem,
  CircularProgress,
  Stack,
  IconButton,
  ListItemAvatar,
  Avatar,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import { HugeiconsIcon } from "@hugeicons/react";
import { Delete02Icon, PlusSignIcon } from "@hugeicons/core-free-icons";
import { getInitial } from "@/utils/names";
import { useDeleteBranchMutation } from "@/features/organizations/api/branchApi";
import useSnackbar from "@/hooks/useSnackbar";
import LoadingButton from "@mui/lab/LoadingButton";
import { EmptyState } from "@/components";
import useOrganizations from "@/features/organizations/useOrganizations";
import {
  openBranchDrawer, setActiveBranch,

} from "@/store/uiSlice";
import { useAppDispatch, useAppSelector } from "@/hooks";
import { useAccessControl } from "@/features/access-control";

function stringToColor(string: string) {
  let hash = 0;
  let i;

  /* eslint-disable no-bitwise */
  for (i = 0; i < string.length; i += 1) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }

  let color = "#";

  for (i = 0; i < 3; i += 1) {
    const value = (hash >> (i * 8)) & 0xff;
    color += `00${value.toString(16)}`.slice(-2);
  }
  /* eslint-enable no-bitwise */

  return color;
}

interface Props {
  organizationId: string;
  name?: string;
  value?: string;
  onChange?: (event: React.ChangeEvent<{ name?: string; value: unknown }>, child: React.ReactNode) => void;
}

const OrganizationBranches = ({ organizationId, name, value, onChange }: Props) => {
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const [openSnackbar] = useSnackbar();
  const [deleteBranch, deleteBranchMutation] = useDeleteBranchMutation();
  const { currentOrganization } = useOrganizations();
  const { hasAccess } = useAccessControl();
  const activeBranch = useAppSelector((state) => state.ui.activeBranch);
  const user = useAppSelector((state) => state.auth.user);

  const hasBranchAccess = (branchId: string) => {
    if (user?.access?.[currentOrganization?.id]?.role === "admin" || user?.access?.[currentOrganization?.id]?.role === "super-admin") return true;
    const branches = user?.access?.[currentOrganization?.id]?.branches;
    return branches?.includes(branchId) ?? false;
  };

  const handleDeleteBranch = useCallback(async (id: string) => {
    const response = await deleteBranch({
      organizationId,
      branchId: id,
    });

    if (response?.error) {
      openSnackbar("Error al eliminar la sucursal", "error");
    } else {
      if (activeBranch === id) {
        dispatch(setActiveBranch(null));
      }
      searchParams.delete("selectedBranch");
      setSearchParams(searchParams);
      openSnackbar("Sucursal eliminada", "success");
    }
  }, [activeBranch]);

  if (!currentOrganization?.branches) {
    return (
      <Stack flex={1} padding={2} alignItems="center">
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <div>
      {currentOrganization?.branches?.length ? (
        <>
          <FormControl fullWidth size="small">
            <InputLabel>Sucursal principal</InputLabel>
            <Select label="Sucursal principal" name={name} value={value} onChange={onChange}>
              {currentOrganization?.branches.map((b) => (
                <MenuItem key={b.id} value={b.id}>
                  {b.title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Divider />
          <List>
            {currentOrganization?.branches.map((b) => (
              <ListItem
                key={b.id}
                disablePadding
                secondaryAction={
                  hasAccess("delete.branch") ? deleteBranchMutation.isLoading && deleteBranchMutation.originalArgs?.branchId === b.id ? (
                    <CircularProgress size={24} thickness={4} />
                  ) : (
                    <IconButton edge="end" aria-label="delete" onClick={() => handleDeleteBranch(b.id)}>
                      <HugeiconsIcon icon={Delete02Icon} />
                    </IconButton>
                  ) : null

                }
              >
                <ListItemButton component="a" href="#simple-list" onClick={() => {
                  setSearchParams({ selectedBranch: b.id });
                  dispatch(openBranchDrawer());
                }}
                  disabled={
                    !hasBranchAccess(b.id)
                  }
                >
                  <ListItemAvatar>
                    <Avatar sx={{ backgroundColor: stringToColor(b.title) }}>{getInitial(b.title)}</Avatar>
                  </ListItemAvatar>
                  <ListItemText primary={b.title} secondary={b.location.address} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </>
      ) : (
        <EmptyState title="No hay sucursales" />
      )}

      {hasAccess("create.branch") && (
        <Stack flex={1} alignItems="center" padding={1}>
          <LoadingButton
            size="small"
            variant="contained"
            startIcon={<HugeiconsIcon icon={PlusSignIcon} />}
            onClick={() => {
              searchParams.delete("selectedBranch");
              setSearchParams(searchParams);
              dispatch(openBranchDrawer(null));
            }}
          >
            Agregar Sucursal
          </LoadingButton>
        </Stack>
      )}
    </div>
  );
};

export default OrganizationBranches;
